import { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router';
import { api, getToken, clearToken, setAuthLostHandler } from './api';
import { linkToPath, setPendingDeepLink, takePendingDeepLink } from './deeplink';
import { useApp } from './store';
import { wsClient } from './ws';
import { AuthScreen } from './screens/AuthScreen';
import { AppShell } from './screens/AppShell';
import { ResetPasswordScreen } from './screens/ResetPasswordScreen';
import { VerifyEmailScreen } from './screens/VerifyEmailScreen';
import { Spinner } from './components/ui';
import { VoiceStateProvider } from './components/voiceContext';
import { VoiceRejoinPrompt } from './components/VoiceRejoinPrompt';
import { CallManager } from './components/CallManager';
import { ConnectionBanner } from './components/ConnectionBanner';
import { ToastHost, toast } from './components/Toast';

// The voice/LiveKit surface (~the largest dependency in the bundle) is
// code-split: these components only download once the user is in — or just
// dropped from — a voice call. Both VoiceConnection exports share one chunk.
// Text-only users never fetch any of it. See AuthenticatedShell's `needVoice`
// gate and `voiceContext.tsx` for the architecture. (VoiceRejoinPrompt is
// deliberately NOT lazy — it must be able to appear on a cold reload before
// the heavy chunk loads — and it's livekit-free, so it stays in the main
// bundle.)
// Rarely-visited full-page screens load on demand too: admin is super-admin
// only, and profiles are an occasional detour from chat. Keeps them out of the
// entry chunk every visitor downloads.
const AdminScreen = lazy(() => import('./screens/AdminScreen').then((m) => ({ default: m.AdminScreen })));
const InviteScreen = lazy(() => import('./screens/InviteScreen').then((m) => ({ default: m.InviteScreen })));
const ProfileScreen = lazy(() => import('./screens/ProfileScreen').then((m) => ({ default: m.ProfileScreen })));
const ProfileEditScreen = lazy(() =>
  import('./screens/ProfileEditScreen').then((m) => ({ default: m.ProfileEditScreen })),
);
function RouteFallback() {
  return (
    <div className="h-full w-full flex items-center justify-center bg-bg text-text-muted">
      <Spinner />
    </div>
  );
}

const MeetRoomScreen = lazy(() =>
  import('./screens/MeetRoomScreen').then((m) => ({ default: m.MeetRoomScreen }))
);

const VoiceConnection = lazy(() =>
  import('./components/VoiceConnection').then((m) => ({ default: m.VoiceConnection })),
);
const ScreenSharePopout = lazy(() =>
  import('./components/VoiceConnection').then((m) => ({ default: m.ScreenSharePopout })),
);
const PopoutShareView = lazy(() =>
  import('./components/PopoutShareView').then((m) => ({ default: m.PopoutShareView })),
);

// Desktop (Electron) update check. No-op in a browser (electronAPI undefined)
// and in popout windows. Fully best-effort: any failure is swallowed so it can
// never affect app load. The wrapper re-validates the payload and returns
// { action: 'none' } for anything stale/invalid, so we just ask whenever the
// installed version differs from the backend's published latest.
async function checkDesktopUpdate() {
  const electron = window.electronAPI;
  // Older desktop builds lack these methods — guard so mixed versions are safe.
  if (!electron?.isDesktop || !electron.getAppVersion || !electron.notifyUpdateAvailable) return;
  try {
    const latest = await api.desktopVersion();
    if (!latest.version) return; // backend has no version configured
    const { version: current } = await electron.getAppVersion();
    if (current === latest.version) return; // already up to date
    await electron.notifyUpdateAvailable({
      version: latest.version,
      url: latest.url,
      ...(latest.notes ? { notes: latest.notes } : {}),
    });
  } catch (err) {
    console.warn('Desktop update check failed:', err);
  }
}

export function App() {
  const user = useApp((s) => s.user);
  const setUser = useApp((s) => s.setUser);
  const [bootstrapping, setBootstrapping] = useState(true);

  // Once on load: ask the desktop wrapper to surface an update if one exists.
  // Skipped in popout windows (they share the session and shouldn't prompt).
  useEffect(() => {
    if (new URLSearchParams(window.location.search).has('popout')) return;
    void checkDesktopUpdate();
  }, []);

  // The server ended this session (signed out from another device, password
  // changed elsewhere, or the token expired): drop back to the login screen
  // once, instead of leaving a dead UI whose every request 401s and whose
  // socket reconnects forever. Both api.ts (a 401 from requireAuth) and ws.ts
  // (close code 4001/1008) report here.
  useEffect(() => {
    const signedOut = () => {
      if (!getToken()) return; // already handled (several 401s can race)
      clearToken();
      wsClient.disconnect();
      setUser(null);
      toast.info('You were signed out. Sign in again to continue.');
    };
    setAuthLostHandler(signedOut);
    wsClient.onAuthLost = signedOut;
    return () => {
      setAuthLostHandler(null);
      wsClient.onAuthLost = null;
    };
  }, [setUser]);

  // On mount: if we have a token, try to fetch the user. If that fails, clear and show login.
  useEffect(() => {
    const token = getToken();
    if (!token) {
      setBootstrapping(false);
      return;
    }
    api.me()
      .then(setUser)
      .catch(() => {
        clearToken();
        setUser(null);
      })
      .finally(() => setBootstrapping(false));
  }, [setUser]);

  if (bootstrapping) {
    return (
      <div className="h-full w-full flex items-center justify-center text-text-muted">
        <Spinner size={20} />
      </div>
    );
  }

  // Public-route gate: /reset-password/:token works whether or not the user
  // is signed in. Wrapping in BrowserRouter unconditionally means we can
  // serve that route even when there's no auth, while everything else
  // still routes through AuthenticatedShell when signed in.
  return (
    <BrowserRouter>
      <DeepLinkHandler />
      <RootRouter user={user} />
    </BrowserRouter>
  );
}

// Soft "please verify your email" banner. Track-only: it never blocks the
// app, it just nudges + offers a one-click resend. Hidden once verified (or
// for accounts grandfathered as verified). Dismissible for the session.
function VerifyEmailBanner() {
  const user = useApp((s) => s.user);
  const [dismissed, setDismissed] = useState(false);
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  if (!user || user.emailVerified !== false || dismissed) return null;

  async function resend() {
    setState('sending');
    try {
      await api.resendVerification();
      setState('sent');
    } catch {
      setState('error');
    }
  }

  return (
    <div className="flex items-center gap-3 px-3 py-1.5 text-xs bg-accent/15 border-b border-accent/30 text-text">
      <span className="flex-1 min-w-0">
        {state === 'sent'
          ? 'Verification email sent — check your inbox (and spam).'
          : 'Please verify your email address to secure your account.'}
      </span>
      {state !== 'sent' && (
        <button
          onClick={resend}
          disabled={state === 'sending'}
          className="font-semibold underline hover:no-underline disabled:opacity-50"
        >
          {state === 'sending' ? 'Sending…' : state === 'error' ? 'Try again' : 'Resend email'}
        </button>
      )}
      <button
        onClick={() => setDismissed(true)}
        className="text-text-muted hover:text-text"
        title="Dismiss"
        aria-label="Dismiss"
      >
        ✕
      </button>
    </div>
  );
}

// Routes OS deep links (chatdoof:// or intercepted https://chat.doof.lol/*)
// into in-app navigation. Mounted once, stably, inside the Router so it
// survives the AuthScreen ↔ authenticated-shell swap. No-op in a browser
// (electronAPI undefined) and in popout windows. A link that arrives while
// signed out is stashed and consumed once the user authenticates.
function DeepLinkHandler() {
  const navigate = useNavigate();
  const user = useApp((s) => s.user);

  // Subscribe to deep links from the wrapper. The wrapper replays the launch
  // url on first subscription, so cold-start links aren't lost.
  useEffect(() => {
    const electron = window.electronAPI;
    if (!electron?.onDeepLink) return;
    // Popout windows must not hijack-navigate — they're viewer-only.
    if (new URLSearchParams(window.location.search).has('popout')) return;
    return electron.onDeepLink((url) => {
      const path = linkToPath(url);
      if (!path) return; // unrecognized / off-origin → ignore, stay put
      if (useApp.getState().user) navigate(path);
      else setPendingDeepLink(path); // defer until authenticated
    });
  }, [navigate]);

  // Consume a stashed link once authed — covers both "already signed in at
  // launch" and "link arrived on the login screen, then signed in".
  useEffect(() => {
    if (!user) return;
    const path = takePendingDeepLink();
    if (path) navigate(path);
  }, [user, navigate]);

  return null;
}

function RootRouter({ user }: { user: ReturnType<typeof useApp.getState>['user'] }) {
  const location = useLocation();

  // Popout mode (desktop wrapper feature). When the URL has `?popout=<sid>`,
  // we're an Electron-spawned popout window: render only the viewer-only
  // PopoutShareView. We do this BEFORE auth/reset gates because the
  // popout always runs with auth (its parent main-window session shares
  // cookies/token via the same Electron session). If the user somehow
  // isn't authed (cookies blocked etc), PopoutShareView shows an error
  // and self-closes.
  const params = new URLSearchParams(location.search);
  const popoutShareId = params.get('popout');
  const popoutChannelId = params.get('room');
  if (popoutShareId && popoutChannelId) {
    return (
      <Suspense
        fallback={
          <div className="h-full w-full flex items-center justify-center text-text-muted">
            <Spinner size={20} />
          </div>
        }
      >
        <PopoutShareView shareId={popoutShareId} channelId={popoutChannelId} />
      </Suspense>
    );
  }

  // The reset-password route is always available, regardless of auth state.
  // Admins can hand the link to anyone — including users who got locked out
  // and can't sign in.
  if (location.pathname.startsWith('/reset-password/')) {
    return (
      <Routes>
        <Route path="/reset-password/:token" element={<ResetPasswordScreen />} />
      </Routes>
    );
  }

  // Email verification works whether or not the user is signed in — the link
  // arrives by email and consumes a single-use token regardless of session.
  if (location.pathname.startsWith('/verify-email/')) {
    return (
      <Routes>
        <Route path="/verify-email/:token" element={<VerifyEmailScreen />} />
      </Routes>
    );
  }
  // Instant meeting rooms are reachable with no account at all, so this branch
  // must sit BEFORE the auth gate below — same as reset-password and
  // verify-email above. A signed-in visitor lands here too and joins as
  // themselves; MeetRoomScreen handles both.
  if (location.pathname.startsWith('/join/')) {
    return (
      <Suspense
        fallback={
          <div className="h-full w-full flex items-center justify-center text-text-muted">
            <Spinner size={20} />
          </div>
        }
      >
        <Routes>
          <Route path="/join/:code" element={<MeetRoomScreen />} />
        </Routes>
      </Suspense>
    );
  }

  if (!user) {
    // A shared /invite/:code link opened while signed out: the sign-in screen
    // names the server and prefills the code for sign-up. The URL stays put,
    // so once signed in the authenticated /invite/:code route takes over.
    const invite = /^\/invite\/([^/]+)/.exec(location.pathname)?.[1];
    return <AuthScreen invite={invite} />;
  }
  return <AuthenticatedShell />;
}

// Wraps the routes with WS lifecycle that persists across navigation —
// crucial so navigating to /admin (a different top-level component) doesn't
// disconnect and re-connect the WS, which would lose any presence/voice
// events that happened in between.
function AuthenticatedShell() {
  const user = useApp((s) => s.user);
  const setUser = useApp((s) => s.setUser);

  useEffect(() => {
    wsClient.connect();
    return () => wsClient.disconnect();
  }, []);

  // Cross-tab/own-update sync: when the backend broadcasts user.updated for
  // the currently signed-in user, refresh the local store. Lets one tab's
  // avatar change immediately reflect in another open tab.
  useEffect(() => {
    if (!user) return;
    const off = wsClient.on((event) => {
      if (event.type !== 'user.updated') return;
      if (event.user.id !== user.id) return;
      setUser({
        ...user,
        username: event.user.username,
        displayName: event.user.displayName,
        avatarUrl: event.user.avatarUrl,
      });
    });
    return off;
  }, [user, setUser]);

  // Tab title reflects unread state. When the user has any unread messages
  // (across all channels and DMs) we prefix the title with a count, like
  //   "(3) doof.lol"
  // so the count is visible in the browser tab strip even when this tab
  // isn't focused. Mentions get a "•" prefix to make them stand out from
  // ordinary unread chatter.
  useEffect(() => {
    function applyTitle(unread: Record<string, { unreadCount: number; mentionCount: number }>) {
      const totals = Object.values(unread);
      const unreadChannels = totals.reduce((sum, u) => sum + (u.unreadCount > 0 ? 1 : 0), 0);
      const mentionTotal = totals.reduce((sum, u) => sum + u.mentionCount, 0);
      const base = 'doof.lol';
      if (mentionTotal > 0) {
        document.title = `• (${mentionTotal}) ${base}`;
      } else if (unreadChannels > 0) {
        document.title = `(${unreadChannels}) ${base}`;
      } else {
        document.title = base;
      }
    }
    // Apply once on mount, then on every store update.
    applyTitle(useApp.getState().unreadByChannel);
    return useApp.subscribe((state) => applyTitle(state.unreadByChannel));
  }, []);

  // Presence: keep online-user state synced regardless of which route is
  // mounted. Previously this listener lived inside AppShell, so navigating
  // to /admin would tear it down and people coming online wouldn't update
  // anywhere until you came back.
  const setUserOnline = useApp((s) => s.setUserOnline);
  const setOnlineUserIds = useApp((s) => s.setOnlineUserIds);
  useEffect(() => {
    return wsClient.on((event) => {
      if (event.type === 'hello') {
        if (event.onlineUserIds) {
          setOnlineUserIds(event.onlineUserIds);
        }
      } else if (event.type === 'presence.update') {
        setUserOnline(event.userId, event.status === 'online');
      }
    });
  }, [setUserOnline, setOnlineUserIds]);

  // Whether to mount the heavy voice/LiveKit surface at all. True while in a
  // call, or briefly after a drop while the rejoin prompt is still offerable
  // (<2.5min, matching VoiceRejoinPrompt's window). Sticky once true: we keep
  // the driver mounted for the rest of the session so the connection lifecycle
  // matches the original always-mounted component. Text-only users never flip
  // this, so they never download the voice chunk.
  const activeVoice = useApp((s) => s.activeVoice);
  const lastVoiceChannelId = useApp((s) => s.lastVoiceChannelId);
  const lastVoiceChannelAt = useApp((s) => s.lastVoiceChannelAt);
  const [needVoice, setNeedVoice] = useState(false);
  useEffect(() => {
    const recentRejoin =
      !!lastVoiceChannelId && !!lastVoiceChannelAt && Date.now() - lastVoiceChannelAt < 150_000;
    if (activeVoice || recentRejoin) setNeedVoice(true);
  }, [activeVoice, lastVoiceChannelId, lastVoiceChannelAt]);
  const isDesktop = !!window.electronAPI?.isDesktop;

  // ConnectionBanner and the Routes are ALWAYS rendered at stable positions
  // (indices 0 and 2) under VoiceStateProvider; the voice surface is a
  // conditional middle slot. This keeps the app tree structurally stable —
  // joining/leaving a call never remounts the routes — while the heavy chunk
  // mounts only when needVoice flips true. The driver publishes its api up to
  // VoiceStateProvider via useVoicePublish().
  return (
    <VoiceStateProvider>
      <ConnectionBanner />
      <VerifyEmailBanner />
      {/* Transient failure feedback. Portaled to document.body, so its position
          here only needs to be inside the Router, not any particular layout. */}
      <ToastHost />
      {/* Eager + ungated: must be able to offer a rejoin on a cold reload,
          before the heavy voice chunk loads. Rejoining sets activeVoice, which
          flips needVoice and mounts the lazy driver below. */}
      <VoiceRejoinPrompt />
      {/* DM call coordinator: incoming/outgoing ring UX + ringtones. Lives
          here so it's always mounted (within the Router) regardless of route. */}
      <CallManager />
      {needVoice && (
        <Suspense fallback={null}>
          <VoiceConnection />
          {/* In-app draggable popout — web-only. On desktop the popout is a
              separate Electron window managed by the wrapper, so we don't want
              the in-app popout floating around at the same time (duplicate
              playback, weird focus interactions). */}
          {!isDesktop && <ScreenSharePopout />}
        </Suspense>
      )}
      <Routes>
        <Route path="/s/:serverId/:channelSlug" element={<AppShell />} />
        <Route path="/s/:serverId" element={<AppShell />} />
        <Route path="/dm/:dmChannelId" element={<AppShell />} />
        <Route path="/dm" element={<AppShell />} />
        <Route path="/friends" element={<AppShell />} />
        <Route path="/u/:username" element={<Suspense fallback={<RouteFallback />}><ProfileScreen /></Suspense>} />
        <Route path="/invite/:code" element={<Suspense fallback={<RouteFallback />}><InviteScreen /></Suspense>} />
        <Route path="/settings/profile" element={<Suspense fallback={<RouteFallback />}><ProfileEditScreen /></Suspense>} />
        <Route path="/admin" element={<Suspense fallback={<RouteFallback />}><AdminScreen /></Suspense>} />
        <Route path="/" element={<AppShell />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </VoiceStateProvider>
  );
}
